import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { businessApi, formatApiError } from "../../lib/ledgerApi";
import HashString from "../../components/HashString";
import { Button } from "../../components/ui/button";
import { Wallet as WalletIcon, Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const fmtNum = (s) => {
  try {
    const n = Number(s);
    if (Number.isFinite(n))
      return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
  } catch {}
  return s;
};

export default function AppWallets() {
  const [wallets, setWallets] = useState([]);
  const [revealed, setRevealed] = useState({});

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const w = await businessApi.wallets();
        if (alive) setWallets(w);
      } catch {}
    };
    load();
    const i = setInterval(load, 3500);
    return () => {
      alive = false;
      clearInterval(i);
    };
  }, []);

  const reveal = async (currency) => {
    try {
      const data = await businessApi.exportKey(currency);
      setRevealed((s) => ({ ...s, [currency]: data.private_key }));
      toast.success(`${currency} private key revealed`, {
        description: "Treat it like cash. Anyone with this key can move funds.",
      });
    } catch (e) {
      toast.error("Could not reveal", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  return (
    <div className="max-w-6xl px-6 md:px-10 py-10 md:py-14">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">/ Wallets</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
          <WalletIcon className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
          Multi-currency wallets
        </h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
          One ledger address per currency. Private keys are encrypted at rest with Fernet
          (custodial model — Phase 1). FX between currencies arrives in Phase 2.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        {wallets.map((w) => (
          <div key={w.currency} data-testid={`app-wallet-${w.currency}`} className="p-5 border border-border rounded-sm bg-card flex flex-col gap-3">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{w.currency}</div>
                <div className="font-mono text-3xl mt-1">{fmtNum(w.balance)}</div>
              </div>
              <Link
                to={`/wallets/${w.ledger_address}`}
                data-testid={`app-wallet-explorer-${w.currency}`}
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              >
                Explorer <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
              </Link>
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Address</div>
            <HashString value={w.ledger_address} label="Address" testid={`app-wallet-address-${w.currency}`} />

            <div className="border-t border-border pt-3 mt-1 text-xs">
              {revealed[w.currency] ? (
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-status-tampered">Private key</div>
                  <div className="break-all font-mono p-2 border border-status-tampered/30 bg-status-tampered/5 rounded-sm text-status-tampered">{revealed[w.currency]}</div>
                  <button
                    onClick={() => setRevealed((s) => { const n = {...s}; delete n[w.currency]; return n; })}
                    className="text-[11px] inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                  >
                    <EyeOff className="w-3 h-3" strokeWidth={1.5} /> Hide
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => reveal(w.currency)}
                  data-testid={`app-wallet-reveal-${w.currency}`}
                  className="text-[11px] inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                >
                  <Eye className="w-3 h-3" strokeWidth={1.5} /> Reveal private key
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
